/**
 * Connection Fields - Power and USB source/port selection
 */

import React from 'react';
import { Box, Flex, Text, Select, Card, Badge } from '@radix-ui/themes';
import { Device } from '../../types';
import { getDevicePortSummary } from '../../utils/portTracking';

interface PowerPort {
  outputIndex: number;
  portIndex: number;
  label: string;
  voltage: number;
  maxCurrent: number;
  globalIndex: number;
}

interface UsbPort {
  outputIndex: number;
  portIndex: number;
  version: string;
  powered: boolean;
  globalIndex: number;
}

interface ConnectionFieldsProps {
  // Power connection
  connectedTo: string | null;
  onConnectedToChange: (deviceId: string | null) => void;
  selectedPowerPort: number | null;
  onPowerPortChange: (portIndex: number | null) => void;
  availableParentDevices: Device[];
  selectedSourceDevice: Device | null;
  availablePowerPorts: PowerPort[];
  
  // USB connection
  usbConnectedTo: string | null;
  onUsbConnectedToChange: (deviceId: string | null) => void;
  selectedUsbPort: number | null;
  onUsbPortChange: (portIndex: number | null) => void;
  availableUsbSourceDevices: Device[];
  selectedUsbSourceDevice: Device | null;
  availableUsbPorts: UsbPort[];
  
  // For port usage summary
  devices: Device[];
  editingDeviceId?: string;
}

const ConnectionFields: React.FC<ConnectionFieldsProps> = ({
  connectedTo,
  onConnectedToChange,
  selectedPowerPort,
  onPowerPortChange,
  availableParentDevices,
  selectedSourceDevice,
  availablePowerPorts,
  usbConnectedTo,
  onUsbConnectedToChange,
  selectedUsbPort,
  onUsbPortChange,
  availableUsbSourceDevices,
  selectedUsbSourceDevice,
  availableUsbPorts,
  devices,
  editingDeviceId,
}) => {
  return (
    <Flex direction="column" gap="3">
      {/* Power Source Selection */}
      <Box>
        <Text as="label" size="2" weight="bold" htmlFor="connectedTo">
          Connected To (Power Source)
        </Text>
        <Select.Root value={connectedTo || 'none'} onValueChange={(value) => {
          onConnectedToChange(value === 'none' ? null : value);
          onPowerPortChange(null); // Reset port selection
        }}>
          <Select.Trigger id="connectedTo" placeholder="Not Connected" style={{ width: '100%' }} mt="1" />
          <Select.Content>
            <Select.Item value="none">None (No Power Connection)</Select.Item>
            <Select.Separator />
            {availableParentDevices.map(d => (
              <Select.Item key={d.id} value={d.id}>
                {d.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text as="p" size="1" color="gray" mt="1">
          Select the power source or distribution device
        </Text>
      </Box>

      {/* Power Port Selection */}
      {connectedTo && selectedSourceDevice && availablePowerPorts.length > 0 && (
        <>
          <Box>
            <Text as="label" size="2" weight="bold">
              Power Port
            </Text>
            <Select.Root 
              value={selectedPowerPort === null ? 'none' : String(selectedPowerPort)} 
              onValueChange={(value) => onPowerPortChange(value === 'none' ? null : Number(value))}
            >
              <Select.Trigger placeholder="Select port" style={{ width: '100%' }} mt="1" />
              <Select.Content>
                <Select.Item value="none">None (No Power Connection)</Select.Item>
                <Select.Separator />
                {availablePowerPorts.map((port) => {
                  const portSummary = getDevicePortSummary(selectedSourceDevice, devices);
                  const portUsage = portSummary.powerPorts[port.globalIndex];
                  const isUsed = portUsage?.used && portUsage.connectedDeviceId !== editingDeviceId;
                  
                  return (
                    <Select.Item 
                      key={port.globalIndex} 
                      value={String(port.globalIndex)}
                      disabled={isUsed}
                    >
                      Port {port.portIndex + 1}: {port.label} ({port.voltage}V @ {port.maxCurrent}A)
                      {isUsed && ` - Used by ${portUsage.connectedDeviceName}`}
                    </Select.Item>
                  );
                })}
              </Select.Content>
            </Select.Root>
            <Text as="p" size="1" color="gray" mt="1">
              Select which power output port to use
            </Text>
          </Box>

          {/* Port availability summary for power source */}
          <Card size="1">
            <Flex direction="column" gap="2">
              <Text size="1" weight="bold">Port Usage on {selectedSourceDevice.name}:</Text>
              {(() => {
                const portSummary = getDevicePortSummary(selectedSourceDevice, devices);
                return (
                  <>
                    {portSummary.powerPortsTotal > 0 && (
                      <Text size="1">
                        ⚡ Power: {portSummary.powerPortsUsed}/{portSummary.powerPortsTotal} ports used
                        {portSummary.powerPortsUsed >= portSummary.powerPortsTotal && (
                          <Badge color="red" ml="2" size="1">Full</Badge>
                        )}
                      </Text>
                    )}
                  </>
                );
              })()}
            </Flex>
          </Card>
        </>
      )}

      {/* USB Source Selection */}
      <Box>
        <Text as="label" size="2" weight="bold" htmlFor="usbConnectedTo">
          USB Source (Optional)
        </Text>
        <Select.Root value={usbConnectedTo || 'none'} onValueChange={(value) => {
          onUsbConnectedToChange(value === 'none' ? null : value);
          onUsbPortChange(null); // Reset USB port selection
        }}>
          <Select.Trigger id="usbConnectedTo" placeholder="No USB Connection" style={{ width: '100%' }} mt="1" />
          <Select.Content>
            <Select.Item value="none">None (No USB Connection)</Select.Item>
            <Select.Separator />
            {availableUsbSourceDevices.map(d => (
              <Select.Item key={d.id} value={d.id}>
                {d.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        <Text as="p" size="1" color="gray" mt="1">
          Select USB hub or computer (can be different from power source)
        </Text>
      </Box>

      {/* USB Port Selection */}
      {usbConnectedTo && selectedUsbSourceDevice && availableUsbPorts.length > 0 && (
        <>
          <Box>
            <Text as="label" size="2" weight="bold">
              USB Port
            </Text>
            <Select.Root 
              value={selectedUsbPort === null ? 'none' : String(selectedUsbPort)} 
              onValueChange={(value) => onUsbPortChange(value === 'none' ? null : Number(value))}
            >
              <Select.Trigger placeholder="Select USB port" style={{ width: '100%' }} mt="1" />
              <Select.Content>
                <Select.Item value="none">None (No USB Connection)</Select.Item>
                <Select.Separator />
                {availableUsbPorts.map((port) => {
                  const portSummary = getDevicePortSummary(selectedUsbSourceDevice, devices);
                  const portUsage = portSummary.usbPorts[port.globalIndex];
                  const isUsed = portUsage?.used && portUsage.connectedDeviceId !== editingDeviceId;
                  
                  return (
                    <Select.Item 
                      key={port.globalIndex} 
                      value={String(port.globalIndex)}
                      disabled={isUsed}
                    >
                      USB Port {port.portIndex + 1}: {port.version} {port.powered ? '(Powered)' : ''}
                      {isUsed && ` - Used by ${portUsage.connectedDeviceName}`}
                    </Select.Item>
                  );
                })}
              </Select.Content>
            </Select.Root>
            <Text as="p" size="1" color="gray" mt="1">
              Select which USB port to connect to
            </Text>
          </Box>

          {/* Port availability summary for USB source */}
          <Card size="1">
            <Flex direction="column" gap="2">
              <Text size="1" weight="bold">Port Usage on {selectedUsbSourceDevice.name}:</Text>
              {(() => {
                const portSummary = getDevicePortSummary(selectedUsbSourceDevice, devices);
                return (
                  <>
                    {portSummary.usbPortsTotal > 0 && (
                      <Text size="1">
                        🔌 USB: {portSummary.usbPortsUsed}/{portSummary.usbPortsTotal} ports used
                        {portSummary.usbPortsUsed >= portSummary.usbPortsTotal && (
                          <Badge color="red" ml="2" size="1">Full</Badge>
                        )}
                      </Text>
                    )}
                  </>
                );
              })()}
            </Flex>
          </Card>
        </>
      )}
    </Flex>
  );
};

export default ConnectionFields;
